import { Box, Heading, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import React from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { agentScopeSuffix, useAgentName, useAnswersPerAgent } from '../../hooks/analytics/useAnalytics'
import { AgentFilter } from '../../types/analyticsTypes'
import { useChartPalette } from './useChartPalette'
import { WidgetError, WidgetLoading } from './WidgetStatus'

// Colours follow agent-name order within the agents shown.
// Not the feedback donut's green, grey and red, which mean thumbs up / none / down on this page.
const AGENT_COLORS = ['#3182CE', '#D69E2E', '#805AD5', '#DD6B20', '#319795', '#D53F8C', '#2C5282', '#B7791F']

interface WeekBar {
  label: string
  answers: Record<string, number>
}

interface AgentTotals {
  key: string
  name: string
  answers: number
  good: number
  neutral: number
  bad: number
}

const formatWeekLabel = (day: string): string =>
  new Date(day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })

const RoutingSection: React.FC<{ agentId: AgentFilter }> = ({ agentId }) => {
  const palette = useChartPalette()
  const agentName = useAgentName(agentId)
  const { data: rows = [], isLoading, isError } = useAnswersPerAgent(agentId)

  if (isLoading) {
    return <WidgetLoading />
  }
  if (isError) {
    return <WidgetError />
  }

  const totals = new Map<string, AgentTotals>()
  const weeks = new Map<string, WeekBar>()
  rows.forEach(row => {
    const week = weeks.get(row.week_start) ?? { label: formatWeekLabel(row.week_start), answers: {} }
    weeks.set(row.week_start, week)
    if (!row.answers) return
    // By id: names are not unique, and recharts reads a string dataKey as a path.
    const key = row.agent_id ?? 'unknown'
    const name = row.agent_name ?? 'Unknown agent'
    const total = totals.get(key) ?? { key, name, answers: 0, good: 0, neutral: 0, bad: 0 }
    total.answers += row.answers
    total.good += row.good
    total.neutral += row.neutral
    total.bad += row.bad
    totals.set(key, total)
    week.answers[key] = (week.answers[key] ?? 0) + row.answers
  })
  const agents = Array.from(totals.values()).sort((a, b) => a.name.localeCompare(b.name))

  return (
    <Box bg='app.bg' borderWidth='1px' borderColor='app.border' borderRadius='lg' p={5}>
      <Heading size='sm' mb={1}>
        Routing: answers per agent{agentScopeSuffix(agentName)}
      </Heading>
      <Text fontSize='sm' color='app.text.muted' mb={4}>
        By the agent that gave the answer, also within a main chat conversation. Last {weeks.size} weeks.
      </Text>
      {agents.length === 0 && <Text color='app.text.muted'>No answers yet.</Text>}
      {agents.length > 0 && (
        <>
          <Box height='280px' mb={6}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={Array.from(weeks.values())} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke={palette.grid} />
                <XAxis dataKey='label' fontSize={12} stroke={palette.grid} tick={{ fill: palette.tick }} />
                <YAxis allowDecimals={false} fontSize={12} stroke={palette.grid} tick={{ fill: palette.tick }} />
                <Tooltip cursor={{ fill: palette.cursor }} {...palette.tooltipProps} />
                <Legend />
                {agents.map((agent, index) => (
                  <Bar
                    key={agent.key}
                    dataKey={(week: WeekBar) => week.answers[agent.key] ?? 0}
                    name={agent.name}
                    stackId='answers'
                    fill={AGENT_COLORS[index % AGENT_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box overflowX='auto'>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>Agent</Th>
                  <Th isNumeric>Answers</Th>
                  <Th isNumeric>Thumbs up</Th>
                  <Th isNumeric>No feedback</Th>
                  <Th isNumeric>Thumbs down</Th>
                </Tr>
              </Thead>
              <Tbody>
                {agents.map(agent => (
                  <Tr key={agent.key}>
                    <Td>{agent.name}</Td>
                    <Td isNumeric>{agent.answers}</Td>
                    <Td isNumeric>{agent.good}</Td>
                    <Td isNumeric>{agent.neutral}</Td>
                    <Td isNumeric>{agent.bad}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  )
}

export default RoutingSection
